import PropTypes from 'prop-types'
import { useState } from 'react'
import {
  BarChart3,
  List,
  FileText,
  ChevronDown,
  ChevronRight,
  TrendingUp,
} from 'lucide-react'

/**
 * AiDataSummary Component
 *
 * Renders normalized data_summary structures from the backend.
 * Supports section types: metric, table, list, text, nested, chart_data
 */
export default function AiDataSummary({ data }) {
  // Handle non-normalized data
  if (!data || data.type !== 'data_summary') {
    return null
  }

  const { sections, format, title, metadata } = data

  // Show warning for raw format (normalization failed)
  if (format === 'raw') {
    return (
      <div className="space-y-3">
        {title && <h3 className="font-semibold text-gray-900">{title}</h3>}
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800 mb-2">
            ⚠️ Data structure could not be normalized. Showing raw format.
          </p>
          {metadata?.normalizationError && (
            <p className="text-xs text-yellow-700">
              Error: {metadata.normalizationError}
            </p>
          )}
        </div>
        {sections?.map(section => (
          <SectionRenderer key={section.id} section={section} />
        ))}
      </div>
    )
  }

  // Determine layout based on complexity
  const isSimple = metadata?.complexity === 'low'
  const isComplex = metadata?.complexity === 'high'

  return (
    <div className="space-y-3">
      {title && <h3 className="font-semibold text-gray-900">{title}</h3>}

      {/* Complexity indicator (optional, subtle) */}
      {isComplex && (
        <div className="text-xs text-gray-500">
          Complex data structure ({sections.length} sections)
        </div>
      )}

      {/* Render sections with appropriate layout */}
      <div
        className={
          isSimple
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'
            : 'space-y-4'
        }
      >
        {sections.map(section => (
          <SectionRenderer
            key={section.id}
            section={section}
            simple={isSimple}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * SectionRenderer - Routes to appropriate section component
 */
function SectionRenderer({ section, simple = false }) {
  switch (section.type) {
    case 'metric':
      return <MetricSection section={section} simple={simple} />
    case 'table':
      return <TableSection section={section} />
    case 'list':
      return <ListSection section={section} />
    case 'text':
      return <TextSection section={section} />
    case 'nested':
      return <NestedSection section={section} />
    case 'chart_data':
      return <ChartDataSection section={section} />
    default:
      return (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            Unknown section type: {section.type}
          </p>
        </div>
      )
  }
}

/**
 * MetricSection - Display single numeric or boolean values
 */
function MetricSection({ section }) {
  const { title, content, metadata } = section
  const { value, label, unit } = content

  // Format value
  let displayValue = value
  if (typeof value === 'number') {
    displayValue = value.toLocaleString()
  } else if (typeof value === 'boolean') {
    displayValue = value ? '✓ Yes' : '✗ No'
  }

  return (
    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="text-xs font-medium text-blue-700 uppercase mb-1">
            {title}
          </div>
          <div className="text-2xl font-bold text-blue-900">
            {displayValue}
            {unit && <span className="text-lg ml-1">{unit}</span>}
          </div>
          {label && <div className="text-xs text-blue-600 mt-1">{label}</div>}
          {metadata?.description && (
            <div className="text-xs text-gray-600 mt-1">
              {metadata.description}
            </div>
          )}
        </div>
        <TrendingUp className="w-5 h-5 text-blue-400" />
      </div>
    </div>
  )
}

/**
 * TableSection - Display tabular data with headers and rows
 */
function TableSection({ section }) {
  const { title, content, metadata } = section
  const { headers, rows } = content

  if (!headers || !rows || rows.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {title && (
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-purple-600" />
          <h4 className="font-medium text-gray-900">{title}</h4>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-purple-100">
        <table className="min-w-full divide-y divide-purple-200 bg-purple-50">
          <thead className="bg-purple-100">
            <tr>
              {headers.map((header, idx) => (
                <th
                  key={idx}
                  className="px-4 py-2 text-left text-xs font-semibold text-purple-900 uppercase tracking-wider"
                >
                  {String(header)
                    .replace(/([A-Z])/g, ' $1')
                    .trim()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-purple-100">
            {rows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className="hover:bg-purple-100 transition-colors"
              >
                {row.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap"
                  >
                    {formatCellValue(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {metadata?.truncated && (
        <p className="text-xs text-gray-500">
          Showing {rows.length} of {metadata.originalLength} rows
        </p>
      )}
    </div>
  )
}

/**
 * ListSection - Display arrays or key-value pairs
 */
function ListSection({ section }) {
  const { title, content, metadata } = section
  const { items } = content

  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {title && (
        <div className="flex items-center gap-2">
          <List className="w-4 h-4 text-green-600" />
          <h4 className="font-medium text-gray-900">{title}</h4>
        </div>
      )}

      <ul className="space-y-1.5">
        {items.map((item, idx) => (
          <li
            key={idx}
            className="flex items-start gap-2 p-2 bg-green-50 rounded border border-green-100 text-sm text-gray-900"
          >
            <span className="text-green-600 font-medium">•</span>
            <span className="flex-1">{formatListItem(item)}</span>
          </li>
        ))}
      </ul>

      {metadata?.truncated && (
        <p className="text-xs text-gray-500">
          Showing {items.length} of {metadata.originalLength} items
        </p>
      )}
    </div>
  )
}

/**
 * TextSection - Display text content
 */
function TextSection({ section }) {
  const { title, content } = section

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
      {title && (
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-gray-600" />
          <h4 className="font-medium text-gray-900">{title}</h4>
        </div>
      )}
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{content}</p>
    </div>
  )
}

/**
 * NestedSection - Display complex nested objects with collapsible structure
 */
function NestedSection({ section }) {
  const { title, content, metadata } = section
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="border border-orange-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-orange-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-orange-600" />
          )}
          <h4 className="font-medium text-gray-900">{title}</h4>
        </div>
        <span className="text-xs text-gray-500">
          {metadata?.depth && `Depth: ${metadata.depth}`}
        </span>
      </button>

      {isExpanded && (
        <div className="p-3 bg-white border-t border-orange-200">
          <pre className="text-xs whitespace-pre-wrap break-words font-mono text-gray-800 overflow-x-auto">
            {JSON.stringify(content, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

/**
 * ChartDataSection - Placeholder for chart data (passed through from backend)
 */
function ChartDataSection({ section }) {
  const { title, content } = section

  return (
    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
      {title && (
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-indigo-600" />
          <h4 className="font-medium text-gray-900">{title}</h4>
        </div>
      )}
      <p className="text-sm text-indigo-700 mb-2">
        Chart data structure detected
      </p>
      <pre className="text-xs whitespace-pre-wrap break-words font-mono text-gray-700 bg-white p-2 rounded border border-indigo-100">
        {JSON.stringify(content, null, 2)}
      </pre>
    </div>
  )
}

/**
 * Helper Functions
 */

function formatCellValue(value) {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'number') return value.toLocaleString()
  if (typeof value === 'boolean') return value ? '✓' : '✗'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function formatListItem(item) {
  if (typeof item === 'string' || typeof item === 'number') {
    return String(item)
  }
  if (typeof item === 'object' && item !== null) {
    // For key-value objects, format nicely
    if (Object.keys(item).length <= 3) {
      return Object.entries(item)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')
    }
    return JSON.stringify(item)
  }
  return String(item)
}

/**
 * PropTypes
 */

AiDataSummary.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.oneOf(['data_summary']).isRequired,
    format: PropTypes.oneOf(['simple', 'complex', 'raw']),
    title: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.oneOf([
          'metric',
          'table',
          'list',
          'text',
          'nested',
          'chart_data',
        ]).isRequired,
        title: PropTypes.string,
        content: PropTypes.any.isRequired,
        metadata: PropTypes.object,
      })
    ).isRequired,
    metadata: PropTypes.shape({
      query: PropTypes.string,
      category: PropTypes.string,
      location: PropTypes.object,
      timestamp: PropTypes.string,
      originalStructure: PropTypes.string,
      complexity: PropTypes.oneOf(['low', 'medium', 'high']),
      normalizationError: PropTypes.string,
    }),
  }).isRequired,
}

SectionRenderer.propTypes = {
  section: PropTypes.object.isRequired,
  simple: PropTypes.bool,
}

MetricSection.propTypes = {
  section: PropTypes.object.isRequired,
  simple: PropTypes.bool,
}

TableSection.propTypes = {
  section: PropTypes.object.isRequired,
}

ListSection.propTypes = {
  section: PropTypes.object.isRequired,
}

TextSection.propTypes = {
  section: PropTypes.object.isRequired,
}

NestedSection.propTypes = {
  section: PropTypes.object.isRequired,
}

ChartDataSection.propTypes = {
  section: PropTypes.object.isRequired,
}
